import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  getFile,
  listSessionBrainBankSyncs,
  syncSessionFileToBrainBank,
} from "../service/apiService";
import { addFileToKnowledgeBase, fileIdExists } from "../helpers/knowledgeBaseLocalStorage";
import { useKnowledgeBase } from "./KnowledgeBaseContext";
import { useSession } from "./SessionContext";

// What the BrainBank uploader accepts and the processor indexes. Everything
// else stays in the chat only; the server enforces the same list.
const SYNCABLE_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".txt"];

export const isSyncableAttachment = (fileName: string) =>
  SYNCABLE_EXTENSIONS.some((ext) => (fileName || "").toLowerCase().endsWith(ext));

// Same cadence as BrainBankExportsCard: fast enough to feel live, then slower;
// still processing after five minutes means the BrainBank page, not this chip.
const FAST_INTERVAL_MS = 3000;
const SLOW_INTERVAL_MS = 10000;
const SLOW_AFTER_MS = 30000;
const POLL_CEILING_MS = 5 * 60 * 1000;

export type AttachmentSyncStatus =
  | "sending" // the sync call is in flight
  | "checking" // restored after a reload, first status read pending
  | "not_processed" // in the BrainBank, queued
  | "processing"
  | "stalled" // still processing when status checks stopped; re-read on reopen
  | "processed"
  | "failed" // the BrainBank could not process it
  | "removed" // deleted through the BrainBank UI
  | "send_failed" // the sync call itself failed; can be retried
  | "no_permission" // the user cannot add to this BrainBank
  | "not_uploader"; // only the member who uploaded a file can add it

export interface AttachmentSync {
  status: AttachmentSyncStatus;
  knowledgeBaseId: string;
  knowledgeBaseName: string;
  displayName: string;
  kbFileId?: string;
  // Only syncs started on this page announce themselves (toast, bell). A
  // status restored after a reload is shown on the chip and nowhere else.
  notify: boolean;
}

interface AttachmentFile {
  file_name: string;
  original_file_name?: string;
}

interface TargetBrainBank {
  id: string;
  name: string;
}

interface BrainBankSyncContextProps {
  /** The composer's "Save to BrainBank" switch. In memory only, per chat. */
  saveToBrainBank: boolean;
  setSaveToBrainBank: (value: boolean) => void;
  getAttachmentSync: (sessionId: string, fileName: string) => AttachmentSync | undefined;
  /** Whether this conversation's existing records have been loaded. */
  syncsLoaded: (sessionId: string) => boolean;
  syncAttachments: (
    sessionId: string,
    files: AttachmentFile[],
    brainBank: TargetBrainBank
  ) => void;
  retryAttachmentSync: (sessionId: string, fileName: string) => void;
  earlierFilesDismissed: (sessionId: string) => boolean;
  dismissEarlierFiles: (sessionId: string) => void;
}

const BrainBankSyncContext = createContext<BrainBankSyncContextProps | undefined>(undefined);

export const useBrainBankSync = (): BrainBankSyncContextProps => {
  const context = useContext(BrainBankSyncContext);
  if (!context) {
    throw new Error("useBrainBankSync must be used within a BrainBankSyncProvider");
  }
  return context;
};

const keyOf = (sessionId: string, fileName: string) => `${sessionId}::${fileName}`;

const newRequestId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const TERMINAL: AttachmentSyncStatus[] = ["processed", "failed", "removed"];

/**
 * Chat attachments that are also added to the conversation's BrainBank.
 *
 * The copy is made after Send, one server call per file; the chat never waits
 * on it. Status is read from the BrainBank file the same way the meetings
 * export card reads it, and announced only through what already exists: the
 * useFileUpload toast wording and the bell's BrainBank upload rows.
 *
 * Entries are keyed by conversation and stored file name and survive moving
 * between conversations, so a file still processing when the user leaves a
 * chat still announces itself when it finishes.
 */
export const BrainBankSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionId } = useSession();
  const { selectedKnowledgeBase } = useKnowledgeBase();

  const [saveToBrainBank, setSaveToBrainBank] = useState(true);
  const [syncs, setSyncs] = useState<Record<string, AttachmentSync>>({});
  const syncsRef = useRef(syncs);
  const [loadedSessions, setLoadedSessions] = useState<Set<string>>(() => new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // The ref is written first and synchronously: the polling loop reads it
  // straight after an update, before React has necessarily rendered, and a
  // functional setState updater is not guaranteed to have run by then.
  const commit = useCallback((next: Record<string, AttachmentSync>) => {
    syncsRef.current = next;
    setSyncs(next);
  }, []);

  const update = useCallback(
    (key: string, patch: Partial<AttachmentSync> & Pick<AttachmentSync, "status">) => {
      const current = syncsRef.current[key];
      if (!current) return;
      commit({ ...syncsRef.current, [key]: { ...current, ...patch } });
    },
    [commit]
  );

  // The switch is a per-chat decision: a new BrainBank, or another
  // conversation, starts from the default again. The first send of a new chat
  // also changes sessionId (from "" to the new row); that is the same chat.
  const previousSessionIdRef = useRef(sessionId);
  useEffect(() => {
    const previous = previousSessionIdRef.current;
    previousSessionIdRef.current = sessionId;
    if (previous && previous !== sessionId) setSaveToBrainBank(true);
  }, [sessionId]);
  useEffect(() => {
    setSaveToBrainBank(true);
  }, [selectedKnowledgeBase?.id]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const announce = useCallback((entry: AttachmentSync, file: any) => {
    if (!entry.notify) return;
    if (file && fileIdExists(file.id)) {
      addFileToKnowledgeBase(file);
      window.dispatchEvent(new CustomEvent("knowledgeBaseStorageUpdate"));
    }
    // The wording useFileUpload already uses for the same events.
    if (entry.status === "processed") {
      toast.success(`File "${entry.displayName}" processed successfully.`);
    } else if (entry.status === "failed") {
      toast.info(`Processing failed for "${entry.displayName}".`);
    }
  }, []);

  const startPolling = useCallback(
    (key: string) => {
      if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
      const startedAt = Date.now();
      let addedToBell = false;

      const poll = async () => {
        delete timersRef.current[key];
        const entry = syncsRef.current[key];
        if (!entry?.kbFileId || TERMINAL.includes(entry.status)) return;
        if (Date.now() - startedAt > POLL_CEILING_MS) {
          // Stop asking, but say so rather than spin forever; reopening the
          // conversation reads it again.
          update(key, { status: "stalled" });
          return;
        }

        // A hidden tab throttles timers anyway; back off rather than queue a
        // burst of requests for when it wakes.
        if (typeof document !== "undefined" && document.hidden) {
          timersRef.current[key] = setTimeout(poll, SLOW_INTERVAL_MS);
          return;
        }

        let status: AttachmentSyncStatus = entry.status;
        let file: any = null;
        try {
          file = await getFile(entry.kbFileId);
          status = (file?.status as AttachmentSyncStatus) || "not_processed";
          if (entry.notify && !addedToBell && file) {
            // Straight into the bell as "In Queue", the way a Settings upload
            // appears; the bell refreshes its own rows when opened.
            addedToBell = true;
            addFileToKnowledgeBase(file);
            window.dispatchEvent(new CustomEvent("knowledgeBaseStorageUpdate"));
          }
        } catch (err: any) {
          // Deleted through the BrainBank UI -- say so rather than spin.
          if (err?.response?.status === 404) status = "removed";
        }

        if (status !== entry.status) {
          update(key, { status });
          if (TERMINAL.includes(status)) {
            announce({ ...entry, status }, file);
            return;
          }
        }

        const elapsed = Date.now() - startedAt;
        timersRef.current[key] = setTimeout(
          poll,
          elapsed > SLOW_AFTER_MS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS
        );
      };

      poll();
    },
    [announce, update]
  );

  const syncOne = useCallback(
    async (sessionIdToUse: string, file: AttachmentFile, brainBank: TargetBrainBank) => {
      const key = keyOf(sessionIdToUse, file.file_name);
      const base = {
        knowledgeBaseId: brainBank.id,
        knowledgeBaseName: brainBank.name,
        displayName: file.original_file_name || file.file_name,
        notify: true,
      };
      commit({ ...syncsRef.current, [key]: { ...base, status: "sending" } });

      try {
        const result = await syncSessionFileToBrainBank(
          sessionIdToUse,
          file.file_name,
          brainBank.id,
          newRequestId()
        );
        update(key, {
          status: "not_processed",
          kbFileId: result.sync.file_id,
          // A copy made earlier is not news: no toast, no new bell row.
          notify: result.status !== "already_sent",
        });
        startPolling(key);
      } catch (err: any) {
        const code = err?.response?.status;
        const detail = String(err?.response?.data?.detail || "");
        let status: AttachmentSyncStatus = "send_failed";
        // The server's two 403s: the file is another member's, or the
        // BrainBank is not one this user can add to.
        if (code === 403 && detail.includes("add this file")) status = "not_uploader";
        else if (code === 403 || code === 423) status = "no_permission";
        update(key, { status });
      }
    },
    [commit, startPolling, update]
  );

  const syncAttachments = useCallback(
    (sessionIdToUse: string, files: AttachmentFile[], brainBank: TargetBrainBank) => {
      files
        .filter((file) => isSyncableAttachment(file.file_name))
        .forEach((file) => syncOne(sessionIdToUse, file, brainBank));
    },
    [syncOne]
  );

  const retryAttachmentSync = useCallback(
    (sessionIdToUse: string, fileName: string) => {
      const entry = syncsRef.current[keyOf(sessionIdToUse, fileName)];
      if (!entry) return;
      syncOne(
        sessionIdToUse,
        { file_name: fileName, original_file_name: entry.displayName },
        { id: entry.knowledgeBaseId, name: entry.knowledgeBaseName }
      );
    },
    [syncOne]
  );

  // Reopening a conversation shows its attachments' BrainBank status again.
  // Once per conversation per page load; after that the entries are live here.
  const loadedSessionsRef = useRef(loadedSessions);
  loadedSessionsRef.current = loadedSessions;
  useEffect(() => {
    if (!sessionId) return;
    if (loadedSessionsRef.current.has(sessionId)) {
      // Anything whose status checks gave up is read again on return.
      Object.entries(syncsRef.current).forEach(([key, entry]) => {
        if (key.startsWith(`${sessionId}::`) && entry.status === "stalled") startPolling(key);
      });
      return;
    }
    let cancelled = false;
    listSessionBrainBankSyncs(sessionId)
      .then((files) => {
        if (cancelled) return;
        const restored: string[] = [];
        const next = { ...syncsRef.current };
        files.forEach((file) => {
          const key = keyOf(sessionId, file.file_name);
          const latest = file.syncs[file.syncs.length - 1];
          // Something started on this page is newer than the record.
          if (!latest || next[key]) {
            if (next[key]?.status === "stalled") restored.push(key);
            return;
          }
          next[key] = {
            status: "checking",
            knowledgeBaseId: latest.knowledge_base_id,
            knowledgeBaseName: latest.knowledge_base_name,
            displayName: file.original_file_name,
            kbFileId: latest.file_id,
            notify: false,
          };
          restored.push(key);
        });
        commit(next);
        restored.forEach(startPolling);
        // Only a successful read counts as loaded: the earlier-files banner
        // relies on it to know which files are already in the BrainBank.
        setLoadedSessions((prev) => new Set(prev).add(sessionId));
      })
      .catch(() => {
        // Status is a convenience here; the chat works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [commit, sessionId, startPolling]);

  const getAttachmentSync = useCallback(
    (sessionIdToUse: string, fileName: string) => syncs[keyOf(sessionIdToUse, fileName)],
    [syncs]
  );
  const syncsLoaded = useCallback(
    (sessionIdToUse: string) => loadedSessions.has(sessionIdToUse),
    [loadedSessions]
  );
  const earlierFilesDismissed = useCallback(
    (sessionIdToUse: string) => dismissed.has(sessionIdToUse),
    [dismissed]
  );
  const dismissEarlierFiles = useCallback((sessionIdToUse: string) => {
    setDismissed((prev) => new Set(prev).add(sessionIdToUse));
  }, []);

  const value = useMemo(
    () => ({
      saveToBrainBank,
      setSaveToBrainBank,
      getAttachmentSync,
      syncsLoaded,
      syncAttachments,
      retryAttachmentSync,
      earlierFilesDismissed,
      dismissEarlierFiles,
    }),
    [
      saveToBrainBank,
      getAttachmentSync,
      syncsLoaded,
      syncAttachments,
      retryAttachmentSync,
      earlierFilesDismissed,
      dismissEarlierFiles,
    ]
  );

  return (
    <BrainBankSyncContext.Provider value={value}>
      {children}
    </BrainBankSyncContext.Provider>
  );
};

export default BrainBankSyncProvider;
